import { Navigate } from 'react-router';
import { Code2, Eye, ShieldAlert } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { useDeveloperMode } from '@/hooks/use-developer-mode';

/**
 * Settings → Developer.
 *
 * Hosts the single switch that reveals the workflow/graph internals. Restricted to
 * super admins: implementors author SOPs and must not be able to grant themselves
 * edit access to the generated workflow or graph.
 */
export default function DeveloperSettings() {
	const { isDeveloperMode, canUseDeveloperMode, setDeveloperMode } = useDeveloperMode();

	// Not a developer → the page doesn't exist for them.
	if (!canUseDeveloperMode) return <Navigate to='/settings/users' replace />;

	const handleToggle = (checked: boolean) => {
		setDeveloperMode(checked);
		toast.success(checked ? 'Developer mode on — workflow and graph editing unlocked' : 'Developer mode off');
	};

	return (
		<div className='flex flex-col w-full'>
			<div className='sticky top-0 backdrop-blur-[6px] bg-white/80 border-b border-gray-500 flex items-center h-[60px] px-4 shrink-0 z-10'>
				<p className='text-base font-semibold text-ink leading-6'>Developer</p>
			</div>

			<div className='flex flex-col gap-6 p-4'>
				{/* ── Developer mode toggle ── */}
				<div className='w-[600px] border border-gray-300 rounded-2xl overflow-hidden p-3 flex items-center gap-8'>
					<div className='flex flex-col gap-1.5 flex-1 min-w-0'>
						<p className='flex items-center gap-2 text-[13px] text-ink leading-[18px]'>
							<Code2 className='size-3.5 text-gray-800' />
							Developer mode
						</p>
						<p className='text-[12px] text-gray-800 leading-4'>
							Unlocks editing of the generated workflow and its execution graph, plus manual regeneration. Leave this off for
							normal use — the protocol is the source of truth and its graph is compiled from it.
						</p>
					</div>
					<Switch
						className='cursor-pointer shrink-0'
						checked={isDeveloperMode}
						onCheckedChange={handleToggle}
						aria-label='Developer mode'
					/>
				</div>

				{/* ── What changes ── */}
				<div className='w-[600px] flex flex-col gap-3'>
					<p className='text-[12px] font-medium text-gray-800 uppercase tracking-wide'>What this changes</p>

					<div className='flex gap-3 rounded-xl border border-gray-300 p-3'>
						<Eye className='size-4 text-gray-800 shrink-0 mt-0.5' />
						<div className='flex flex-col gap-1'>
							<p className='text-[13px] text-ink leading-[18px]'>Off — read-only (default)</p>
							<p className='text-[12px] text-gray-800 leading-4'>
								Protocols are edited in their editor. Workflow and Graph appear as read-only tabs on a protocol&apos;s text, and
								are enabled or disabled by the protocol&apos;s own toggle.
							</p>
						</div>
					</div>

					<div className='flex gap-3 rounded-xl border border-gray-300 p-3'>
						<ShieldAlert className='size-4 text-amber-500 shrink-0 mt-0.5' />
						<div className='flex flex-col gap-1'>
							<p className='text-[13px] text-ink leading-[18px]'>On — editable</p>
							<p className='text-[12px] text-gray-800 leading-4'>
								Adds &ldquo;Open editor&rdquo; on the Workflow and Graph tabs and re-enables the standalone Workflows section.
								Hand-edits to the graph can drift from the protocol, so prefer changing the protocol and compiling it.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
